/**
 * Mid-section prose-block reveal — pin + SplitText headline (+ optional aside),
 * then prose fade, then optional CTA stagger. Play-once on enter (not scrubbed).
 * Tuned for pin + title + paragraph blocks of this content size (Intro part2,
 * State threatened / red-list intro, Conclusion reach). See docs/04-motion.md.
 *
 * Chapter SectionHeaders use `section-header-reveal.ts` instead.
 */

export const proseBlockReveal = {
  /**
   * ScrollTrigger start — block top crosses this viewport line, then the
   * timed timeline plays once (no scrub).
   *
   * Keep this at/below ~55% so play-once reveals do not finish while the
   * previous band still owns the viewport (`top 75%` felt early — Feedbucket
   * “section animates in early”).
   */
  scrollTriggerStart: 'top 55%',
  /**
   * Location pin — scale/fade at the same time as the headline segment.
   */
  pin: {
    duration: 0.5,
    fromScale: 0,
    ease: 'back.out(1.7)',
  },
  /**
   * Headline units: SplitText characters for Latin locales and intact words
   * for Arabic, with `mask: "lines"` (overflow:clip per line). Visual knobs
   * mirror section-header reveals; `duration` is seconds (play-once).
   */
  headline: {
    duration: 0.25,
    /**
     * Vertical offset as % of the reveal-unit box (start → 0). Higher = more
     * of the glyph sits below the line overflow:clip mask at the start.
     */
    yPercent: 45,
    /** Rotation in degrees (start → 0). */
    rotationDeg: 11,
    /** LTR pivot — left centre, matches reference `transform-origin: 0% 50%`. */
    transformOriginLtr: '0% 50%',
    /** RTL mirror of the LTR origin. */
    transformOriginRtl: '100% 50%',
    /** Stagger between reveal units within the headline segment (seconds). */
    stagger: 0.015,
    /**
     * Extra translateY (px) per unit index. Keep at 0 for play-once so every
     * unit travels the same distance in the same duration (a non-zero value
     * makes later units look faster). Scrubbed section headers can still use a
     * cascade offset in `section-header-reveal.ts`.
     */
    staggerOffsetYPx: 0,
    /** Linear per-unit motion — the same velocity curve for every target. */
    ease: 'none',
    mask: 'lines' as const,
  },
  /**
   * Aside / bubble (e.g. threatened StatCircle) — fades + subtle scale with the
   * headline (same moment as the pin). Scale owned here so play-once GSAP stays
   * in sync; `<StatCircleReveal animateOnView={false}>` when wrapped.
   */
  aside: {
    duration: 0.85,
    offsetY: 24,
    /** Subtle start scale — matches spring-badge / docs/04-motion.md. */
    fromScale: 0.85,
    ease: 'power2.out',
  },
  /**
   * Prose — fade/rise after the headline. Starts slightly before the last
   * letters finish (`earlyByHeadlineFraction`).
   */
  prose: {
    duration: 1.2,
    offsetY: 24,
    ease: 'power2.out',
    /**
     * Start the prose fade this fraction of the headline segment *before*
     * the headline would finish (0 = after finish, 0.3 ≈ near the end).
     */
    earlyByHeadlineFraction: 0.05,
  },
  /**
   * CTA buttons — fade/rise after prose, staggered slightly.
   */
  actions: {
    duration: 0.4,
    offsetY: 24,
    stagger: 0.08,
    ease: 'power2.out',
  },
} as const;
